import { create } from 'zustand';
import type { SessionUser } from '@dgm/shared';
import { api } from './api.js';
import { maybeCaptureDeviceTelemetry } from './device-telemetry.js';

/**
 * Auth store. The SPA holds only the lightweight SessionUser in memory — never
 * tokens (those live in HttpOnly cookies). On boot we call `/me`; a 200 means
 * the session cookies are valid, a 401 means logged out.
 */
/** /me returns more than SessionUser — account lifecycle status + onboarding. */
type MeResponse = SessionUser & {
  status?: string;
  onboarded?: boolean;
  preferences?: { analyticsOptIn?: boolean } | null;
};

interface AuthState {
  user: SessionUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  /** True when the account is in the deletion grace period (A3). */
  pendingDeletion: boolean;
  /** True once the user has completed first-run onboarding (A6). */
  onboarded: boolean;
  hydrate: () => Promise<void>;
  setUser: (user: SessionUser | null) => void;
  reactivate: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
  logout: () => Promise<void>;
}

function deriveFlags(me: MeResponse | null) {
  return {
    pendingDeletion: me?.status === 'pending_deletion',
    // Treat unknown as onboarded so the wizard never blocks an existing user
    // whose /me predates the field; only an explicit `false` shows it.
    onboarded: me?.onboarded !== false,
  };
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'loading',
  pendingDeletion: false,
  onboarded: true,
  hydrate: async () => {
    try {
      const me = await api.get<MeResponse>('/me');
      set({ user: me, status: 'authenticated', ...deriveFlags(me) });
      // Once-per-session device-capability capture (CA-005) — consent-gated,
      // fire-and-forget; never delays the session becoming usable.
      maybeCaptureDeviceTelemetry(me.preferences?.analyticsOptIn);
    } catch {
      set({ user: null, status: 'anonymous', pendingDeletion: false, onboarded: true });
    }
  },
  setUser: (user) => {
    set({
      user,
      status: user ? 'authenticated' : 'anonymous',
      // The login path passes the full /me response (flags present at runtime),
      // so the deletion banner / onboarding wizard show right after login.
      ...deriveFlags(user as MeResponse | null),
    });
    // Login is the other session-establishment path (CA-005).
    if (user) maybeCaptureDeviceTelemetry((user as MeResponse).preferences?.analyticsOptIn);
  },
  reactivate: async () => {
    await api.post('/me/reactivate');
    set({ pendingDeletion: false });
  },
  completeOnboarding: async () => {
    await api.post('/me/onboard').catch(() => undefined);
    set({ onboarded: true });
  },
  logout: async () => {
    await api.post('/auth/logout').catch(() => undefined);
    set({ user: null, status: 'anonymous', pendingDeletion: false, onboarded: true });
  },
}));
